import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faCalendarAlt,
  faCreditCard,
  faDownload,
  faEllipsisVertical,
  faEye,
  faExternalLinkAlt,
  faFilter,
  faFileInvoiceDollar,
  faFolderOpen,
  faMoneyCheckAlt,
  faSearch,
  faSort,
  faSortDown,
  faSortUp,
  faSyncAlt,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import {
  exportDetailedPaymentReportCsv,
  getDetailedPaymentReport,
} from "../../services/detailedPaymentReportApi";
import DetailedPaymentReportViewModal from "./DetailedPaymentReportViewModal";
import DprDocumentsModal from "./DprDocumentsModal";

const COLUMNS = [
  { key: "ppd_term", label: "PPD Term" },
  { key: "payment_mode", label: "Payment Mode" },
  { key: "received_date", label: "Received Date" },
  { key: "processed_date", label: "Processed Date" },
  { key: "payment_due_date", label: "Payment Due Date" },
  { key: "tat", label: "TAT" },
  { key: "loa_date_issued", label: "LOA Date Issued" },
  { key: "admission_date", label: "Admission Date" },
  { key: "discharged_date", label: "Discharged Date" },
  { key: "trans_no", label: "Trans No." },
  { key: "ataccess_trans_no", label: "ATACCESS TRN#" },
  { key: "loa_no", label: "LOA" },
  { key: "member_name", label: "Member Name" },
  { key: "member_type", label: "Member Type" },
  { key: "member_id", label: "Member ID" },
  { key: "tin", label: "TIN" },
  { key: "provider_name", label: "Provider" },
  { key: "physician_name", label: "Physician" },
  { key: "coverage_code", label: "Coverage" },
  { key: "transaction_amount", label: "Transaction Amt." },
  { key: "vat_registered", label: "VAT Registered" },
  { key: "net_of_vat", label: "Net of VAT" },
  { key: "vat_amount", label: "VAT Amount" },
  { key: "claim_amount", label: "Actual Claim Amt." },
  { key: "withholding_tax", label: "Withholding Tax" },
  { key: "ppd", label: "PPD" },
  { key: "ppd_withholding_tax", label: "PPD Withholding Tax" },
  { key: "net_payable", label: "Net Payable" },
  { key: "form_no", label: "Form #" },
  { key: "bill_no", label: "Biller Reference" },
  { key: "soa", label: "SOA" },
  { key: "corp_name", label: "Corporate" },
  { key: "credited_date", label: "CV Date(formerly Credit Date)" },
  { key: "check_no", label: "Check No." },
  { key: "cv_no", label: "CV#" },
  { key: "payment_ref_no", label: "Payment Ref. No." },
  { key: "date_paid", label: "Date Paid" },
  { key: "account_name", label: "Account Name" },
  { key: "bank_name", label: "Bank" },
  { key: "account_number", label: "Account No." },
  { key: "mode_of_release", label: "Mode of Release" },
  { key: "transaction_tag", label: "Transaction Tag" },
  { key: "provider_setup", label: "Provider Setup" },
  { key: "claim_remarks", label: "Claim Remarks" },
  { key: "claim_processor", label: "Claim Processor" },
  { key: "_issued_by", label: "Issued By" },
  { key: "scanned_date", label: "Date & Time Scanned" },
  { key: "scanned_by", label: "Scanned/Attached By" },
  { key: "validate_status", label: "Validation Status" },
  { key: "validation_remarks", label: "Validation Remarks" },
  { key: "datetime_validated", label: "Datetime Validated" },
  { key: "validated_by", label: "Validated By" },
  { key: "funding_status", label: "Funding Status" },
  { key: "funded_amt", label: "Funded Amount" },
];

const CURRENCY_KEYS = new Set([
  "transaction_amount",
  "net_of_vat",
  "vat_amount",
  "claim_amount",
  "withholding_tax",
  "ppd",
  "ppd_withholding_tax",
  "net_payable",
  "funded_amt",
]);

const LONG_TEXT_KEYS = new Set([
  "claim_remarks",
  "validation_remarks",
  "account_number",
  "account_name",
  "provider_name",
  "physician_name",
  "member_name",
  "corp_name",
  "soa",
]);

const HEADER_ACTION_BUTTON_BASE =
  "inline-flex h-8 items-center justify-center gap-1.5 rounded-md border px-2.5 text-xs font-semibold shadow-sm transition-all duration-150 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-1";

const DPR_HEADER_NAV_ACTIONS = [
  {
    label: "Upload Validation CSV",
    path: "/dpr-validation-upload",
    icon: faUpload,
    className:
      "border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-100 focus:ring-emerald-500",
  },
  {
    label: "Find Data Using PRN Number",
    path: "/find-data-using-prn",
    icon: faSearch,
    className:
      "border-cyan-200 bg-cyan-50 text-cyan-700 hover:border-cyan-300 hover:bg-cyan-100 focus:ring-cyan-500",
  },
  {
    label: "Payment Calendar",
    path: "/batch-add-holiday-list",
    icon: faCalendarAlt,
    className:
      "border-indigo-200 bg-indigo-50 text-indigo-700 hover:border-indigo-300 hover:bg-indigo-100 focus:ring-indigo-500",
  },
  {
    label: "Credit File",
    path: "/credit-file",
    icon: faCreditCard,
    className:
      "border-sky-200 bg-sky-50 text-sky-700 hover:border-sky-300 hover:bg-sky-100 focus:ring-sky-500",
  },
  {
    label: "Credit Advise",
    path: "/credit-advise",
    icon: faFileInvoiceDollar,
    className:
      "border-violet-200 bg-violet-50 text-violet-700 hover:border-violet-300 hover:bg-violet-100 focus:ring-violet-500",
  },
  {
    label: "Bank Validation",
    path: "/provider-bank-validation",
    icon: faMoneyCheckAlt,
    className:
      "border-rose-200 bg-rose-50 text-rose-700 hover:border-rose-300 hover:bg-rose-100 focus:ring-rose-500",
  },
];

const formatCurrency = (value) => {
  const numeric = Number(value ?? 0);
  if (Number.isNaN(numeric)) return value || "";

  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
};

const toDisplayValue = (row, key) => {
  const raw = row?.[key];
  if (raw === null || raw === undefined) return "";
  if (CURRENCY_KEYS.has(key)) return formatCurrency(raw);
  return String(raw);
};

const ACTION_MENU_WIDTH = 288;
const ACTION_MENU_HEIGHT = 220;
const ACTION_MENU_MARGIN = 12;
const ACTION_MENU_GAP = 8;

const getActionMenuPosition = (triggerElement) => {
  const rect = triggerElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let left = rect.right - ACTION_MENU_WIDTH;
  left = Math.max(
    ACTION_MENU_MARGIN,
    Math.min(left, viewportWidth - ACTION_MENU_WIDTH - ACTION_MENU_MARGIN)
  );

  let top = rect.bottom + ACTION_MENU_GAP;
  let placement = "bottom";

  if (top + ACTION_MENU_HEIGHT > viewportHeight - ACTION_MENU_MARGIN) {
    top = rect.top - ACTION_MENU_HEIGHT - ACTION_MENU_GAP;
    placement = "top";
  }

  const maxTop = Math.max(
    ACTION_MENU_MARGIN,
    viewportHeight - ACTION_MENU_HEIGHT - ACTION_MENU_MARGIN
  );
  top = Math.max(ACTION_MENU_MARGIN, Math.min(top, maxTop));

  return { top, left, placement };
};

const getRowActionKey = (row) => String(row?.tbl_id || row?.trans_no || row?.payment_ref_no || "");

const DPR_TABLE_STATE_STORAGE_KEY = "providerPayment.dpr.tableState.v1";
const DPR_LIST_ROUTE = "/transaction-reimbursement-dpr";

const formatDateForInput = (value) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDateOffsetYmd = (offsetDays) => {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  return formatDateForInput(date);
};

const createDefaultDprTableState = () => {
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

  return {
    search: "",
    appliedSearch: "",
    dateFrom: formatDateForInput(monthStart),
    dateTo: formatDateForInput(today),
    page: 1,
    pageSize: 10,
    sortBy: "processed_date",
    sortDirection: "desc",
  };
};

const DEFAULT_DPR_TABLE_STATE = createDefaultDprTableState();

const normalizeDprTableState = (state = {}) => {
  const defaultState = createDefaultDprTableState();
  const normalizedSearch = String(state.search || "");
  const normalizedAppliedSearch = String(
    state.appliedSearch !== undefined ? state.appliedSearch : state.search || ""
  ).trim();

  return {
    search: normalizedSearch,
    appliedSearch: normalizedAppliedSearch,
    dateFrom: String(state.dateFrom || defaultState.dateFrom),
    dateTo: String(state.dateTo || defaultState.dateTo),
    page: Math.max(1, Number(state.page || defaultState.page)),
    pageSize: Math.max(1, Number(state.pageSize || defaultState.pageSize)),
    sortBy: String(state.sortBy || defaultState.sortBy),
    sortDirection: state.sortDirection === "asc" ? "asc" : "desc",
  };
};

const loadPersistedDprTableState = () => {
  if (typeof window === "undefined") {
    return DEFAULT_DPR_TABLE_STATE;
  }

  try {
    const rawState = window.sessionStorage.getItem(DPR_TABLE_STATE_STORAGE_KEY);
    if (!rawState) {
      return DEFAULT_DPR_TABLE_STATE;
    }

    return normalizeDprTableState(JSON.parse(rawState));
  } catch (error) {
    return DEFAULT_DPR_TABLE_STATE;
  }
};

const persistDprTableState = (state) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(
      DPR_TABLE_STATE_STORAGE_KEY,
      JSON.stringify(normalizeDprTableState(state))
    );
  } catch (error) {
    // Ignore storage failures so DPR viewing remains usable.
  }
};

const TableCellContent = ({ row, column }) => {
  const value = toDisplayValue(row, column.key);
  const shouldUseScrollableText = LONG_TEXT_KEYS.has(column.key) || value.length > 48;

  if (!value) {
    return <span className="text-slate-400">N/A</span>;
  }

  if (shouldUseScrollableText) {
    return (
      <div
        className="max-h-20 min-w-[12rem] max-w-[22rem] overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs leading-5 text-slate-700"
        title={value}
      >
        {value}
      </div>
    );
  }

  return (
    <div className="max-w-[12rem] truncate whitespace-nowrap" title={value}>
      {value}
    </div>
  );
};

const DetailedPaymentReportPage = () => {
  const navigate = useNavigate();
  const initialTableState = useMemo(() => loadPersistedDprTableState(), []);
  const today = useMemo(() => getDateOffsetYmd(0), []);
  const yesterday = useMemo(() => getDateOffsetYmd(-1), []);
  const last7Start = useMemo(() => getDateOffsetYmd(-6), []);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState(initialTableState.search);
  const [appliedSearch, setAppliedSearch] = useState(initialTableState.appliedSearch);
  const [dateFrom, setDateFrom] = useState(initialTableState.dateFrom);
  const [dateTo, setDateTo] = useState(initialTableState.dateTo);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);

  const [isDocsModalOpen, setIsDocsModalOpen] = useState(false);
  const [docsModalRow, setDocsModalRow] = useState(null);

  const [page, setPage] = useState(initialTableState.page);
  const [pageSize, setPageSize] = useState(initialTableState.pageSize);
  const [sortBy, setSortBy] = useState(initialTableState.sortBy);
  const [sortDirection, setSortDirection] = useState(initialTableState.sortDirection);

  const [pagination, setPagination] = useState({
    page: initialTableState.page,
    page_size: initialTableState.pageSize,
    total: 0,
    total_pages: 1,
  });
  const [resolvedSqlQuery, setResolvedSqlQuery] = useState(null);

  const hasSearchFilter = appliedSearch !== "";
  const hasCompleteDateRange = dateFrom !== "" && dateTo !== "";
  const canFetchRows = hasSearchFilter || hasCompleteDateRange;
  const currentTableState = useMemo(
    () => ({
      search,
      appliedSearch,
      dateFrom,
      dateTo,
      page,
      pageSize,
      sortBy,
      sortDirection,
    }),
    [search, appliedSearch, dateFrom, dateTo, page, pageSize, sortBy, sortDirection]
  );
  const activeQuickRange = useMemo(() => {
    if (dateFrom === today && dateTo === today) return "today";
    if (dateFrom === yesterday && dateTo === yesterday) return "yesterday";
    if (dateFrom === last7Start && dateTo === today) return "last7";
    return "";
  }, [dateFrom, dateTo, today, yesterday, last7Start]);

  const applyQuickRange = useCallback((rangeType) => {
    if (rangeType === "today") {
      setDateFrom(today);
      setDateTo(today);
      setPage(1);
      return;
    }

    if (rangeType === "yesterday") {
      setDateFrom(yesterday);
      setDateTo(yesterday);
      setPage(1);
      return;
    }

    if (rangeType === "last7") {
      setDateFrom(last7Start);
      setDateTo(today);
      setPage(1);
    }
  }, [last7Start, today, yesterday]);

  useEffect(() => {
    const nextAppliedSearch = search.trim();
    if (nextAppliedSearch === appliedSearch) {
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      setAppliedSearch(nextAppliedSearch);
      setPage(1);
    }, 250);

    return () => window.clearTimeout(timerId);
  }, [search, appliedSearch]);

  const fetchDprRows = async () => {
    if (!canFetchRows) {
      setRows([]);
      setPagination({
        page: 1,
        page_size: pageSize,
        total: 0,
        total_pages: 1,
      });
      return;
    }

    setLoading(true);
    try {
      const response = await getDetailedPaymentReport({
          search: appliedSearch,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          page,
          page_size: pageSize,
          sort_by: sortBy,
          sort_direction: sortDirection,
      });

      const payload = response?.data || {};
      setRows(Array.isArray(payload?.data) ? payload.data : []);
      setResolvedSqlQuery(payload?.resolved_sql_query || null);
      setPagination(
        payload?.pagination || {
          page,
          page_size: pageSize,
          total: 0,
          total_pages: 1,
        }
      );
    } catch (error) {
      if (error?.response?.status === 401) {
        window.location.href = "/login";
        return;
      }

      setRows([]);
      setResolvedSqlQuery(error?.response?.data?.resolved_sql_query || null);
      setPagination({
        page,
        page_size: pageSize,
        total: 0,
        total_pages: 1,
      });

      Swal.fire({
        icon: "error",
        title: "Failed to load DPR",
        text: error?.response?.data?.message || "Unable to load detailed payment report data.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewRow = (row) => {
    setSelectedRow(row);
    setIsViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedRow(null);
  };

  const handleViewDocuments = (row) => {
    setDocsModalRow(row);
    setIsDocsModalOpen(true);
  };

  const handleViewFullPage = (row) => {
    persistDprTableState(currentTableState);
    navigate(`${DPR_LIST_ROUTE}/full-view`, {
      state: {
        row,
        columns: COLUMNS,
        returnTo: DPR_LIST_ROUTE,
        tableState: currentTableState,
      },
    });
  };

  const closeActionMenu = useCallback(() => {
    setOpenActionMenu(null);
    setActionMenuPosition(null);
  }, []);

  const handleActionMenuToggle = useCallback((row, event) => {
    event.stopPropagation();
    const rowKey = getRowActionKey(row);

    if (openActionMenu === rowKey) {
      closeActionMenu();
      return;
    }

    setOpenActionMenu(rowKey);
    setActionMenuPosition(getActionMenuPosition(event.currentTarget));
  }, [closeActionMenu, openActionMenu]);

  useEffect(() => {
    if (!openActionMenu) {
      return undefined;
    }

    const handleViewportChange = () => closeActionMenu();
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [closeActionMenu, openActionMenu]);

  const getRowActions = (row) => [
    {
      key: "view",
      label: "View details",
      icon: faEye,
      color: "text-sky-600",
      onClick: () => handleViewRow(row),
    },
    {
      key: "files",
      label: "View LOA & SOA files",
      icon: faFolderOpen,
      color: "text-violet-600",
      onClick: () => handleViewDocuments(row),
    },
    {
      key: "full-page",
      label: "View in full page",
      icon: faExternalLinkAlt,
      color: "text-rose-600",
      isNew: true,
      showExternalIcon: true,
      onClick: () => handleViewFullPage(row),
    },
  ];

  useEffect(() => {
    persistDprTableState(currentTableState);
  }, [currentTableState]);

  useEffect(() => {
    if (!canFetchRows) {
      setRows([]);
      setResolvedSqlQuery(null);
      setPagination({
        page,
        page_size: pageSize,
        total: 0,
        total_pages: 1,
      });
      return;
    }

    fetchDprRows();
  }, [page, pageSize, appliedSearch, dateFrom, dateTo, sortBy, sortDirection, canFetchRows]);

  const totalPages = useMemo(
    () => Math.max(1, Number(pagination?.total_pages || 1)),
    [pagination]
  );

  const startRow = useMemo(() => {
    if (!rows.length) return 0;
    return (page - 1) * pageSize + 1;
  }, [rows, page, pageSize]);

  const endRow = useMemo(() => {
    if (!rows.length) return 0;
    return Math.min(page * pageSize, Number(pagination?.total || 0));
  }, [rows, page, pageSize, pagination]);

  const getSortIcon = (columnKey) => {
    if (sortBy !== columnKey) return faSort;
    return sortDirection === "asc" ? faSortUp : faSortDown;
  };

  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(columnKey);
      setSortDirection("asc");
    }
    setPage(1);
  };

  const handleClearFilters = () => {
    const defaultState = createDefaultDprTableState();
    persistDprTableState({
      ...defaultState,
      pageSize,
    });
    setSearch(defaultState.search);
    setAppliedSearch(defaultState.appliedSearch);
    setDateFrom(defaultState.dateFrom);
    setDateTo(defaultState.dateTo);
    setResolvedSqlQuery(null);
    setSortBy(defaultState.sortBy);
    setSortDirection(defaultState.sortDirection);
    setPage(defaultState.page);
  };

  const handleExportCsv = async () => {
    if (!canFetchRows) {
      await Swal.fire({
        icon: "info",
        title: "Filter required",
        text: "Please enter a search term or select both Processed Date From and Processed Date To before exporting.",
      });
      return;
    }

    const confirm = await Swal.fire({
      icon: "question",
      title: "Export Detailed Payment Report?",
      html: `
        <div style="text-align:left;font-size:14px;line-height:1.55;">
          <p><strong>Summary</strong></p>
          <p>Total matching records: <strong>${Number(pagination?.total || 0).toLocaleString()}</strong></p>
          <p>Date range: <strong>${dateFrom || "Any"}</strong> to <strong>${dateTo || "Any"}</strong></p>
          <p>Search term: <strong>${appliedSearch || "(none)"}</strong></p>
          <p>Sort: <strong>${sortBy}</strong> (${sortDirection.toUpperCase()})</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Generate CSV",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#2563eb",
    });

    if (!confirm.isConfirmed) return;

    try {
      const response = await exportDetailedPaymentReportCsv({
          search: appliedSearch,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          sort_by: sortBy,
          sort_direction: sortDirection,
      });

      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", `dpr_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      if (error?.response?.status === 401) {
        window.location.href = "/login";
        return;
      }

      Swal.fire({
        icon: "error",
        title: "CSV export failed",
        text: "Unable to generate DPR CSV right now.",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-sky-50 to-blue-50 p-4 md:p-6">
      <div className="mx-auto w-full max-w-[96rem] space-y-6">
        <div className="rounded-2xl bg-gradient-to-r from-sky-700 via-cyan-600 to-emerald-500 p-6 text-white shadow-lg">
          <h1 className="text-2xl font-bold tracking-tight">Detailed Payment Report</h1>
          <p className="mt-1 text-sm text-cyan-50">
            Filter, review, export, and validate provider payment report records.
          </p>
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-white/20 px-3 py-1">Auto-refresh filters</span>
            <span className="rounded-full bg-white/20 px-3 py-1">Date-range quick actions</span>
            <span className="rounded-full bg-white/20 px-3 py-1">CSV-ready output</span>
          </div>
        </div>

        <div className="rounded-2xl border border-cyan-100 bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            <FontAwesomeIcon icon={faFilter} />
            Report Filters
          </div>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
            <div className="relative lg:col-span-2">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Search
              </label>
              <FontAwesomeIcon
                icon={faSearch}
                className="pointer-events-none absolute left-3 top-[calc(50%+12px)] -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by trans #, payment ref #, SOA, LOA, sequence"
                className="w-full rounded-lg border border-gray-200 py-2 pl-10 pr-3 text-sm outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Processed Date From
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(event) => {
                  setDateFrom(event.target.value);
                  setPage(1);
                }}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Processed Date To
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(event) => {
                  setDateTo(event.target.value);
                  setPage(1);
                }}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100"
              />
            </div>
          </div>

          <div className="mt-4 overflow-x-auto pb-1">
            <div className="inline-flex min-w-full flex-nowrap items-center gap-2 whitespace-nowrap">
              <button
                type="button"
                onClick={() => applyQuickRange("today")}
                className={`rounded-md px-2.5 py-1.5 text-xs font-semibold transition ${
                  activeQuickRange === "today"
                    ? "bg-cyan-600 text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => applyQuickRange("yesterday")}
                className={`rounded-md px-2.5 py-1.5 text-xs font-semibold transition ${
                  activeQuickRange === "yesterday"
                    ? "bg-cyan-600 text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                Yesterday
              </button>
              <button
                type="button"
                onClick={() => applyQuickRange("last7")}
                className={`rounded-md px-2.5 py-1.5 text-xs font-semibold transition ${
                  activeQuickRange === "last7"
                    ? "bg-cyan-600 text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                Last 7 Days
              </button>
              <button
                type="button"
                onClick={handleClearFilters}
                className="rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={fetchDprRows}
                className={`${HEADER_ACTION_BUTTON_BASE} border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50 focus:ring-slate-500`}
              >
                <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center">
                  <FontAwesomeIcon icon={faSyncAlt} />
                </span>
                <span className="whitespace-nowrap leading-none">Refresh</span>
              </button>
              <button
                type="button"
                onClick={handleExportCsv}
                className={`${HEADER_ACTION_BUTTON_BASE} border-blue-600 bg-blue-600 text-white hover:border-blue-700 hover:bg-blue-700 focus:ring-blue-500`}
              >
                <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center">
                  <FontAwesomeIcon icon={faDownload} />
                </span>
                <span className="whitespace-nowrap leading-none">Export CSV</span>
              </button>
              {DPR_HEADER_NAV_ACTIONS.map((action) => (
                <button
                  key={action.path}
                  type="button"
                  onClick={() => navigate(action.path)}
                  className={`${HEADER_ACTION_BUTTON_BASE} ${action.className}`}
                >
                  <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center">
                    <FontAwesomeIcon icon={action.icon} />
                  </span>
                  <span className="whitespace-nowrap leading-none">{action.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs text-gray-500">
              Results auto-refresh while typing in Search and when Processed Date From/Processed Date To changes.
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-cyan-100 bg-white p-3 shadow-sm">
          <div className="overflow-x-auto rounded-xl border border-gray-100">
            <table className="min-w-[8128px] divide-y divide-gray-200 text-sm">
              
              <thead className="bg-gray-50">
                <tr>
                  {COLUMNS.map((column) => (
                    <th
                      key={column.key}
                      className="w-44 px-3 py-2 text-left align-top font-semibold text-gray-700"
                    >
                      <button
                        type="button"
                        onClick={() => handleSort(column.key)}
                        className="inline-flex max-w-full items-center gap-1 whitespace-normal text-left leading-tight hover:text-blue-700"
                      >
                        {column.label}
                        <FontAwesomeIcon icon={getSortIcon(column.key)} className="text-xs" />
                      </button>
                    </th>
                  ))}
                  <th className="sticky right-0 z-20 min-w-[10rem] whitespace-nowrap border-l border-gray-200 bg-gray-50 px-3 py-2 text-left font-semibold text-gray-700 shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.35)]">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100 bg-white">
                {loading &&
                  [...Array(10)].map((_, index) => (
                    <tr key={`loading-${index}`} className="animate-pulse">
                      {COLUMNS.map((column) => (
                        <td key={`${column.key}-${index}`} className="px-3 py-3">
                          <div className="h-3 w-24 rounded bg-gray-200" />
                        </td>
                      ))}
                      <td className="sticky right-0 z-10 border-l border-gray-100 bg-white px-3 py-3 shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.2)]">
                        <div className="h-8 w-20 rounded-lg bg-gray-200" />
                      </td>
                    </tr>
                  ))}

                {!loading && rows.length === 0 && (
                  <tr>
                    <td
                      className="px-3 py-8 text-center text-sm text-gray-500"
                      colSpan={COLUMNS.length + 1}
                    >
                      {canFetchRows
                        ? "No detailed payment records found for the selected filters."
                        : "Provide a search term or complete date range first to load detailed payment records."}
                    </td>
                  </tr>
                )}

                {!loading &&
                  rows.map((row, rowIndex) => (
                    <tr key={`${row?.tbl_id || "row"}-${rowIndex}`} className="hover:bg-blue-50/40">
                      {COLUMNS.map((column) => (
                        <td
                          key={`${column.key}-${rowIndex}`}
                          className="px-3 py-2 align-top text-gray-700"
                        >
                          <TableCellContent row={row} column={column} />
                        </td>
                      ))}
                      <td className="sticky right-0 z-10 min-w-[10rem] whitespace-nowrap border-l border-gray-100 bg-white px-3 py-2 shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.2)] group-hover:bg-blue-50/40">
                        <div className="relative">
                          <button
                            type="button"
                            aria-label={`Actions for DPR row ${row?.trans_no || row?.payment_ref_no || row?.tbl_id || ""}`}
                            onClick={(event) => handleActionMenuToggle(row, event)}
                            className={`inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold shadow-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                              openActionMenu === getRowActionKey(row)
                                ? "border-blue-400 bg-blue-50 text-blue-700 shadow-blue-100"
                                : "border-gray-300 bg-white text-gray-700 hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                            }`}
                          >
                            <FontAwesomeIcon icon={faEllipsisVertical} className="text-gray-500" />
                            <span>Actions</span>
                          </button>
                          {openActionMenu === getRowActionKey(row) &&
                            actionMenuPosition &&
                            typeof document !== "undefined" &&
                            createPortal(
                              <>
                                <button
                                  type="button"
                                  aria-label="Close DPR actions menu"
                                  className="fixed inset-0 z-[9998] cursor-default bg-transparent"
                                  onClick={closeActionMenu}
                                />
                                <div
                                  className="fixed z-[9999] w-72 overflow-visible"
                                  style={{
                                    top: `${actionMenuPosition.top}px`,
                                    left: `${actionMenuPosition.left}px`,
                                  }}
                                >
                                  <div
                                    className={`absolute right-6 h-3 w-3 rotate-45 border bg-white ${
                                      actionMenuPosition.placement === "top"
                                        ? "-bottom-1.5 border-l-0 border-t-0 border-gray-200"
                                        : "-top-1.5 border-b-0 border-r-0 border-gray-200"
                                    }`}
                                  />
                                  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl shadow-slate-900/15 ring-1 ring-black/5">
                                    <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white px-4 py-3">
                                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        DPR Actions
                                      </p>
                                    </div>
                                    <div className="space-y-1 p-2">
                                      {getRowActions(row).map((action) => (
                                        <button
                                          key={action.key}
                                          type="button"
                                          onClick={() => {
                                            action.onClick();
                                            closeActionMenu();
                                          }}
                                          className="group flex w-full items-center justify-between rounded-lg border border-transparent px-3 py-2.5 text-left text-sm text-gray-700 transition-all duration-150 hover:translate-x-0.5 hover:border-blue-100 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:text-blue-700 hover:shadow-sm"
                                        >
                                          <span className="flex min-w-0 items-center">
                                            <span className="mr-3 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 transition-all duration-150 group-hover:bg-white group-hover:shadow-sm">
                                              <FontAwesomeIcon icon={action.icon} className={`text-sm ${action.color}`} />
                                            </span>
                                            <span className="flex min-w-0 items-center gap-2">
                                              <span className="truncate font-medium">{action.label}</span>
                                              {action.isNew && (
                                                <span className="rounded-full bg-red-600 px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none text-white">
                                                  New
                                                </span>
                                              )}
                                            </span>
                                          </span>
                                          {action.showExternalIcon && (
                                            <FontAwesomeIcon
                                              icon={faExternalLinkAlt}
                                              className="ml-3 text-xs text-gray-400 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-blue-500"
                                            />
                                          )}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </>,
                              document.body
                            )}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="text-sm text-gray-600">
              Showing {startRow.toLocaleString()} to {endRow.toLocaleString()} of {Number(
                pagination?.total || 0
              ).toLocaleString()} records
            </div>

            <div className="flex items-center gap-2">
              <select
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPage(1);
                }}
                className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
              >
                <option value={10}>10 / page</option>
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>

              <button
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page <= 1}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
                Prev
              </button>

              <div className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700">
                Page {page} of {totalPages}
              </div>

              <button
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page >= totalPages}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <DetailedPaymentReportViewModal
        isOpen={isViewModalOpen}
        onClose={handleCloseViewModal}
        row={selectedRow}
        columns={COLUMNS}
        toDisplayValue={toDisplayValue}
        resolvedSqlQuery={resolvedSqlQuery}
        onViewFullPage={handleViewFullPage}
      />

      <DprDocumentsModal
        isOpen={isDocsModalOpen}
        onClose={() => {
          setIsDocsModalOpen(false);
          setDocsModalRow(null);
        }}
        row={docsModalRow}
      />
    </div>
  );
};

export default DetailedPaymentReportPage;
